import type { DataTransfer, TransferProblem, TransferStatus } from '../api/business/types';
import type { MessageKey } from '../i18n/sk';

/** A transfer still on its way — the page asks again until it is not. */
export const isRunning = (status: TransferStatus): boolean =>
  status === 'QUEUED' || status === 'RUNNING' || status === 'VALIDATING' || status === 'APPLYING';

/**
 * Why a chosen file cannot be imported — not a ZIP, empty, or larger than the deployment's `maxMb` (R0042, read from
 * the API; null while it is not known, when the API alone decides) — or null.
 */
export const packageProblem = (
  file: { name: string; size: number },
  maxMb: number | null,
): MessageKey | null => {
  if (!/\.zip$/i.test(file.name)) {
    return 'transfers.notZip';
  }

  return file.size <= 0 || (maxMb !== null && file.size > maxMb * 1024 * 1024) ? 'transfers.tooLarge' : null;
};

/** How far a transfer is, 0…100, or null when there is nothing to count yet. */
export const progressPercent = (
  transfer: Pick<DataTransfer, 'progressDone' | 'progressTotal'>,
): number | null =>
  transfer.progressTotal > 0
    ? Math.min(100, Math.round((transfer.progressDone / transfer.progressTotal) * 100))
    : null;

/** Whether a problem keeps the import from being applied. */
export const hasErrors = (problems: Array<TransferProblem>): boolean =>
  problems.some((problem) => problem.severity === 'ERROR');

/** A problem's parameters as the sentence quotes them; the `key` of a refusal is the API's own, not quoted. */
export const problemParameters = (problem: TransferProblem): Record<string, string | number> =>
  Object.fromEntries(Object.entries(problem.params).filter(([name]) => name !== 'key'));

/** Why the import skipped or failed a product, as a sentence key; a refusal of the API's own is said in general. */
export const outcomeReasonKey = (reason: string): MessageKey => {
  switch (reason) {
    case 'MATCHED':
    case 'NOTHING_EMPTY':
    case 'ARCHIVED':
    case 'INVALID':
    case 'SCHEMA':
      return `transferReason.${reason}`;
    default:
      return 'transferReason.REFUSED';
  }
};
